import { NavLink, useLocation } from "react-router-dom"
import { useQuery } from "urql"
import {
  LayoutDashboard,
  Radio,
  Target,
  Link2,
  Zap,
  Send,
  AlertTriangle,
  PanelLeftClose,
  PanelLeft,
  Building2,
  Webhook,
  Mail,
  KeyRound,
  Bell,
  Bookmark,
  FileJson,
  Code2,
  Activity,
  ScrollText,
  Settings,
  ShieldCheck,
  Satellite,
  ExternalLink,
  Unplug,
  FolderSync,
  Gauge,
} from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { StatusQuery } from "@/lib/graphql/queries"
import { useSession } from "@/hooks/use-session"

const dashboardItem = { to: "/", icon: LayoutDashboard, label: "Dashboard" } as const

const inboundItems = [
  { to: "/sources", icon: Radio, label: "Sources" },
  { to: "/destinations", icon: Target, label: "Destinations" },
  { to: "/subscriptions", icon: Link2, label: "Subscriptions" },
  { to: "/events", icon: Zap, label: "Events" },
  { to: "/deliveries", icon: Send, label: "Deliveries" },
  { to: "/bookmarks", icon: Bookmark, label: "Bookmarks" },
  { to: "/dlq", icon: AlertTriangle, label: "DLQ" },
] as const

const outboundItems = [
  { to: "/applications", icon: Building2, label: "Applications" },
  { to: "/endpoints", icon: Webhook, label: "Endpoints" },
  { to: "/messages", icon: Mail, label: "Messages" },
  { to: "/outbound-deliveries", icon: Send, label: "Deliveries" },
  { to: "/streams", icon: Activity, label: "Streams" },
  { to: "/outbound-dlq", icon: AlertTriangle, label: "DLQ" },
] as const

const settingsItems = [
  { to: "/event-types", icon: FileJson, label: "Event Types" },
  { to: "/transformations", icon: Code2, label: "Transformations" },
  { to: "/api-keys", icon: KeyRound, label: "API Keys" },
  { to: "/alert-rules", icon: Bell, label: "Alert Rules" },
  { to: "/meta-events", icon: Satellite, label: "Meta Events" },
  { to: "/portal-links", icon: ExternalLink, label: "Portal Links" },
  { to: "/tunnels", icon: Unplug, label: "Tunnels" },
  { to: "/audit-logs", icon: ScrollText, label: "Audit Logs" },
  { to: "/verification-test", icon: ShieldCheck, label: "Verification Test" },
  { to: "/config", icon: FolderSync, label: "Config Export" },
  { to: "/ip-allowlist", icon: ShieldCheck, label: "IP Allowlist" },
  { to: "/usage", icon: Gauge, label: "Usage & plan" },
  { to: "/settings", icon: Settings, label: "Settings" },
] as const

const navSections = [
  { label: "Inbound", items: inboundItems },
  { label: "Outbound", items: outboundItems },
  { label: "Settings", items: settingsItems },
] as const

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const [status] = useQuery({ query: StatusQuery })
  const dlqSize = status.data?.status.dlqSize ?? 0
  const outboundDlqSize = status.data?.status.outboundDlqSize ?? 0
  const location = useLocation()
  const session = useSession()
  const features =
    session.status === "authenticated" ? session.user.entitlements?.features : undefined

  // Gated items stay visible (for discoverability) but show a lock + the tier
  // that unlocks them; clicking lands on the upgrade interstitial.
  const gatedNav: Record<string, { flag: keyof NonNullable<typeof features>; tier: string }> = {
    "/portal-links": { flag: "portal_links", tier: "Stream" },
    "/transformations": { flag: "transformations", tier: "Stream" },
    "/alert-rules": { flag: "alerting", tier: "Stream" },
    "/tunnels": { flag: "tunnel", tier: "Stream" },
    "/ip-allowlist": { flag: "ip_allowlist", tier: "Torrent" },
  }
  // Returns the unlocking tier when an item is gated AND not entitled, else "".
  const lockedTier = (to: string) => {
    const g = gatedNav[to]
    return g && features?.[g.flag] === false ? g.tier : ""
  }

  const getDlqSize = (to: string) => {
    if (to === "/dlq") return dlqSize
    if (to === "/outbound-dlq") return outboundDlqSize
    return 0
  }

  const renderItem = (item: { to: string; icon: typeof LayoutDashboard; label: string }) => {
    const Icon = item.icon
    const isActive = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to)
    const badgeCount = getDlqSize(item.to)
    const locked = lockedTier(item.to)

    const link = (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        className={cn(
          "relative flex items-center rounded-lg text-sm font-medium transition-colors",
          collapsed ? "justify-center py-3" : "gap-2.5 px-2.5 py-2",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
        )}
      >
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 rounded-full bg-primary" />
        )}
        <span className="relative shrink-0">
          <Icon className={cn("transition-[width,height] duration-300", collapsed ? "h-6 w-6" : "h-4.5 w-4.5")} />
          {badgeCount > 0 && (
            <span className={cn(
              "absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-destructive border-2 border-sidebar transition-opacity duration-300",
              collapsed ? "opacity-100" : "opacity-0",
            )} />
          )}
        </span>
        {!collapsed && (
          <span className={cn("truncate", locked ? "text-muted-foreground/70" : "flex-1")}>{item.label}</span>
        )}
        {!collapsed && locked && (
          <span className="ml-1.5 shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {locked}
          </span>
        )}
        {!collapsed && !locked && badgeCount > 0 && (
          <span className="flex items-center justify-center min-w-5 h-5 rounded-full bg-destructive/15 text-destructive text-2xs font-semibold tabular-nums px-1">
            {badgeCount}
          </span>
        )}
      </NavLink>
    )

    if (!collapsed) {
      if (locked) {
        return (
          <Tooltip key={item.to} delayDuration={0}>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right" sideOffset={6}>
              Available on {locked}
            </TooltipContent>
          </Tooltip>
        )
      }
      return <div key={item.to}>{link}</div>
    }

    return (
      <Tooltip key={item.to} delayDuration={0}>
        <TooltipTrigger asChild>
          {link}
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={6}>
          {item.label}
          {locked ? ` · ${locked}` : badgeCount > 0 ? ` (${badgeCount})` : ""}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-30 flex flex-col border-r border-border/50 bg-sidebar transition-[width] duration-300 ease-in-out overflow-hidden",
        collapsed ? "w-16" : "w-55",
      )}
    >
      <div className={cn("relative flex items-center h-14 shrink-0", collapsed ? "justify-center px-2" : "px-3")}>
        {collapsed ? (
          <div className="size-8 shrink-0">
            <img src="/favicon.svg" alt="HiveHook" className="hidden h-full w-full dark:block" draggable={false} />
            <img src="/favicon-light.svg" alt="HiveHook" className="h-full w-full dark:hidden" draggable={false} />
          </div>
        ) : (
          <>
            <img src="/logo.svg" alt="HiveHook" className="hidden h-9 w-auto dark:block" draggable={false} />
            <img src="/logo-light.svg" alt="HiveHook" className="h-9 w-auto dark:hidden" draggable={false} />
          </>
        )}
        <div className={cn("absolute bottom-0 h-px bg-border/50", collapsed ? "left-2 right-2" : "left-3 right-3")} />
      </div>

      <nav className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden px-2 pt-2 pb-2 scrollbar-none">
        {renderItem(dashboardItem)}

        {navSections.map((section) => (
          <div key={section.label}>
            <div className={cn("h-px bg-border/50 mt-2 mb-1 transition-[margin] duration-300", collapsed ? "mx-0" : "mx-1")} />
            <div className={cn(
              "text-2xs font-semibold uppercase tracking-widest text-muted-foreground/60 px-2.5 whitespace-nowrap transition-[opacity,max-height,margin-bottom] duration-300 overflow-hidden",
              collapsed ? "opacity-0 max-h-0 mb-0" : "opacity-100 max-h-6 mb-1",
            )}>
              {section.label}
            </div>
            <div className={cn("flex flex-col transition-[gap] duration-300", collapsed ? "gap-0.5" : "gap-2")}>
              {section.items.map((item) => renderItem(item))}
            </div>
          </div>
        ))}
      </nav>

      <div className="shrink-0 px-2">
        <div className={cn("h-px bg-border/50", collapsed ? "mx-0" : "mx-1")} />
      </div>

      <div className="shrink-0 px-2 pt-2 pb-2">
        <button
          type="button"
          className={cn(
            "flex items-center rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-accent/50 w-full",
            collapsed ? "justify-center py-3" : "gap-2.5 px-2.5 py-2",
          )}
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <PanelLeft className="h-6 w-6 shrink-0 transition-[width,height] duration-300" />
          ) : (
            <>
              <PanelLeftClose className="h-4.5 w-4.5 shrink-0 transition-[width,height] duration-300" />
              <span className="flex-1">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  )
}
